// The sign-in block under the wordmark (artboards 1e/1f/1g, handoff 2). The
// 30/70 split, the photograph, the orange datum rule and the foot live in
// index.html and stay put; this only swaps the block per state. One button, no
// email field. Every refusal names the account it refused; only the inactive
// state names Karan and Sanjeet. No domain line on the first attempt — it
// appears solely in the wrong-domain refusal.
import { escapeHtml } from './ui.js';
import { onAspHost } from './auth.js';

// Off ASP's own address (track.aspconsulting.co.uk / localhost) the page is
// Track's, not ASP's (wave 1, 22/10/2026): no consultancy eyebrow ("Designed by Karan Singh"); the site
// photograph stays (Karan 23/10/2026); no ASP names in the help text. ASP's address is unchanged.
const NEUTRAL = () => !onAspHost();
function neutralise(field) {
  if (!field || !NEUTRAL() || field.dataset.neutral) return;
  field.dataset.neutral = '1';
  field.classList.add('si2-neutral');
  const eyebrow = field.querySelector('.si2-eyebrow');
  if (eyebrow) eyebrow.textContent = 'Designed by Karan Singh';   // shown in capitals by .si2-eyebrow (Karan 23/10/2026)
  const note = field.querySelector('#si2-note');
  if (note) note.textContent = 'Use the Google account or the email address your invite was sent to. If it still won’t let you in, ask whoever invited you to send a new invite.';
}

const GOOGLE_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
  <path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.54 5.54 0 0 1-2.4 3.63v3h3.86c2.26-2.09 3.57-5.17 3.57-8.87Z"></path>
  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24Z"></path>
  <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09Z"></path>
  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75Z"></path></svg>`;

/**
 * Render one sign-in state into the block under the wordmark.
 * @param {HTMLElement} block  the #login-block element
 * @param {'default'|'signing'|'wrong'|'closed'|'refused'|'unreachable'|AccessState} state
 * @param {{email?:string, date?:string}} opts
 * @param {{onGoogle?:fn, onSwitch?:fn, onRetry?:fn, onSignOut?:fn, onPasskey?:fn}} handlers
 */
export function renderSignin(block, state, opts = {}, handlers = {}) {
  const field = block.closest('.si2');
  const access = ACCESS_STATES.includes(state);
  if (field) field.className = `si2 state-${state}${NEUTRAL() ? ' si2-neutral' : ''}${access ? ' si2-compact' : ''}`;
  neutralise(field);
  if (access) { renderAccess(block, state, opts, handlers); return; }
  delete block.dataset.family;

  if (state === 'signing') {
    block.innerHTML = `<div class="si2-rise">
      <div class="si2-label">Signing in</div>
      <div class="si2-heading">One moment</div>
      <div class="si2-text">Checking your account and what you have access to.</div>
      <div class="si2-progress"><i></i></div>
    </div>`;
    return;
  }

  if (state === 'wrong') {
    block.innerHTML = `<div class="si2-rise">
      <div class="si2-label attn">Wrong account</div>
      <div class="si2-heading">That is a personal account</div>
      <div class="si2-text">You signed in as <span class="si2-mono">${escapeHtml(opts.email || 'that account')}</span>. Track only accepts <span class="si2-mono">aspconsulting.co.uk</span> accounts.</div>
      <button type="button" class="si2-btn state-btn" id="si-switch"><span>Try a different account</span></button>
    </div>`;
    block.querySelector('#si-switch')?.addEventListener('click', () => handlers.onSwitch && handlers.onSwitch());
    return;
  }

  if (state === 'closed') {
    block.innerHTML = `<div class="si2-rise">
      <div class="si2-label attn">No longer active</div>
      <div class="si2-heading">This account is closed</div>
      <div class="si2-text">Your Track access ended on <span class="si2-mono">${escapeHtml(opts.date || '—')}</span>. Your time and projects are still on file — nothing has been deleted. If this is wrong, ${NEUTRAL() ? 'your company’s Owner' : 'Karan or Sanjeet'} can reopen it.</div>
    </div>`;
    return;
  }

  // The sign-in gate said no (not invited, or the wrong Google for the
  // company). Its own words, and a way to try another account.
  if (state === 'refused') {
    block.innerHTML = `<div class="si2-rise">
      <div class="si2-label attn">Not let in</div>
      <div class="si2-heading">Track didn’t let this account in</div>
      <div class="si2-text">${escapeHtml(opts.message || 'Track is invite-only.')}</div>
      <button type="button" class="si2-btn state-btn" id="si-switch"><span>Use a different account</span></button>
    </div>`;
    block.querySelector('#si-switch')?.addEventListener('click', () => handlers.onSwitch && handlers.onSwitch());
    return;
  }

  if (state === 'unreachable') {
    block.innerHTML = `<div class="si2-rise">
      <div class="si2-label attn">Could not reach Track</div>
      <div class="si2-heading">Google let you in, Track did not answer</div>
      <div class="si2-text">Your sign-in worked. Something between here and the server did not. Nothing is wrong with your account.</div>
      <button type="button" class="si2-btn state-btn" id="si-retry"><span>Try again</span></button>
    </div>`;
    block.querySelector('#si-retry')?.addEventListener('click', () => handlers.onRetry && handlers.onRetry());
    return;
  }

  // default (1e) — no domain line; the block is the tagline + one button.
  block.innerHTML = `
    <div class="si2-tagline si2-rise" style="animation-delay:120ms">${NEUTRAL() ? 'Projects, time, photos and money, in one place.' : 'Enquiries, projects, time and money, in one place.'}</div>
    <button type="button" class="si2-btn si2-rise" id="si-google" style="animation-delay:200ms">${GOOGLE_SVG}<span>Continue with Google</span></button>
    ${handlers.onEmailLink ? `<form class="si2-rise" id="si-link" style="animation-delay:260ms;margin-top:14px;display:flex;flex-direction:column;gap:8px">
      <label class="si2-text" for="si-email">Or get a sign-in link by email</label>
      <input class="f-input" id="si-email" type="email" autocomplete="email" required placeholder="you@example.com" style="min-height:44px">
      <button type="submit" class="si2-btn"><span>Email me a link</span></button>
      <div class="si2-text" id="si-link-note" role="status"></div>
    </form>` : ''}`;
  block.querySelector('#si-google')?.addEventListener('click', () => handlers.onGoogle && handlers.onGoogle());
  block.querySelector('#si-link')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = String(block.querySelector('#si-email').value || '').trim();
    const note = block.querySelector('#si-link-note');
    if (!email) return;
    note.textContent = 'Sending…';
    Promise.resolve(handlers.onEmailLink(email))
      .then(() => { note.textContent = `Check ${email} for a sign-in link. Open it on this device.`; })
      .catch((err) => { note.textContent = (err && err.message) || 'That did not send. Try again.'; });
  });
}


// ── ACCESS STATES (Sign-in and access redesign, 23/10/2026) ──────────────────
// Everything between "Google said yes" and "Track is open" happens on this same
// split page now, instead of a white card on navy (provisioning) or a bare
// admin screen (the console's passkey gate). Each state is drawn ONLY from what
// Track actually has: a piece of the design with no data behind it (a company
// name, who added you, when) is left out rather than invented.
//   setup          p3  steps: Signed in / Finding your invitation / Opening Track
//   joined         p4  company chip (when known) + all steps done
//   notadded       p5  claimAccess "no-record", or the gate's invite-only refusal
//   noroles        p6  claimAccess "no-roles"
//   failed         p7  a step failed or timed out — with a reference to quote
//   admin-confirm  p8  platform admin: confirm it's you (passkey) before the console
//   admin-checking p11 waiting for the passkey
//   admin-failed   p13 the console couldn't check the passkey (+ technical detail)
/** @typedef {'setup'|'joined'|'notadded'|'noroles'|'failed'|'admin-confirm'|'admin-checking'|'admin-failed'} AccessState */
export const ACCESS_STATES = ['setup', 'joined', 'notadded', 'noroles', 'failed', 'admin-confirm', 'admin-checking', 'admin-failed'];

/** A short reference a person can quote ("ACC-7F3A21"), made in the browser. */
export function accessRef() {
  let hex = '';
  try {
    const b = new Uint8Array(3);
    crypto.getRandomValues(b);
    hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  } catch (_) { hex = Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0'); }
  return 'ACC-' + hex.toUpperCase();
}
/** HH:MM, 24-hour, local. */
export function clockTime(d = new Date()) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
/** "Demo Contractor Ltd" → "DC". */
export function initialsOf(name) {
  const words = String(name || '').replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}
/** A role as people say it: "Site lead", not "site-lead". */
const roleWords = (r) => String(r || '').replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const PASSKEY_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true">
  <path d="M12 11v3.5"></path><path d="M8.5 9.5a3.5 3.5 0 0 1 7 0V14a7 7 0 0 1-1 3.6"></path>
  <path d="M5.5 15.5V9.5a6.5 6.5 0 0 1 11.8-3.8"></path><path d="M18.4 9a6.5 6.5 0 0 1 .1 1.2v3"></path>
  <path d="M9 18.5a6 6 0 0 0 .5-2.5v-1"></path></svg>`;

function googleChip(email, notYou) {
  if (!email) return '';
  return `<div class="si2-chip si2-google">
    <span class="si2-chip-g">${GOOGLE_SVG}</span>
    <span class="si2-chip-txt"><span class="si2-chip-name">Signed in with Google</span><span class="si2-chip-sub si2-chip-mail">${escapeHtml(email)}</span></span>
    ${notYou ? '<button type="button" class="si2-link si2-notyou" data-act="switch">Not you?</button>' : ''}
  </div>`;
}
function companyChip(company, sub) {
  if (!company) return '';
  return `<div class="si2-chip si2-company">
    <span class="si2-chip-mono" aria-hidden="true">${escapeHtml(initialsOf(company))}</span>
    <span class="si2-chip-txt"><span class="si2-chip-name">${escapeHtml(company)}</span>${sub ? `<span class="si2-chip-sub">${escapeHtml(sub)}</span>` : ''}</span>
  </div>`;
}
/** steps: [{label, state:'done'|'active'|'todo'|'fail', sub?}] */
function stepList(steps, prev = []) {
  // A tick or a (!) lands once: a step already in that state when the block is
  // redrawn holds still (is-steady), so only the step that just changed moves.
  return `<ol class="si2-steps">${steps.map((st, i) => `<li class="si2-step is-${st.state}${prev[i] === st.state ? ' is-steady' : ''}"${st.state === 'active' ? ' aria-current="step"' : ''}>
      <span class="si2-step-dot" aria-hidden="true">${st.state === 'done' ? '<svg viewBox="0 0 16 16" width="12" height="12"><path d="M3.5 8.4l3 3 6-6.4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>' : st.state === 'fail' ? '!' : ''}</span>
      <span class="si2-step-txt"><span class="si2-step-label">${escapeHtml(st.label)}${st.state === 'fail' ? '<span class="si2-sr"> — did not finish</span>' : ''}</span>${st.sub ? `<span class="si2-step-sub">${escapeHtml(st.sub)}</span>` : ''}</span>
    </li>`).join('')}</ol>`;
}
const head = (label, heading, attn) => `<div class="si2-label${attn ? ' attn' : ''}">${escapeHtml(label)}</div>
  <h1 class="si2-heading">${escapeHtml(heading)}</h1>`;

function copyText(text, btn, done = 'Copied') {
  const put = navigator.clipboard && navigator.clipboard.writeText
    ? navigator.clipboard.writeText(text)
    : Promise.reject(new Error('no clipboard'));
  put.then(() => {
    if (!btn) return;
    const span = btn.querySelector('span') || btn;
    const was = span.textContent;
    span.textContent = done;
    setTimeout(() => { span.textContent = was; }, 1600);
  }).catch(() => { /* nothing to copy into — the text is on screen to select */ });
}

/** The gate's technical words, as something a person can read (p13). */
function passkeyWords(raw, host) {
  const rp = String(raw || '').match(/RP ID "([^"]+)" is invalid/i);
  if (rp) {
    return `Passkeys only work on the web address they were made for. This one is tied to <strong>${escapeHtml(rp[1])}</strong>, not <strong>${escapeHtml(host)}</strong>. Nothing is wrong with your passkey or your account.`;
  }
  if (/no passkey support/i.test(String(raw || ''))) {
    return 'This browser can’t use passkeys. Open the console in an up-to-date Chrome, Safari or Edge. Nothing is wrong with your account.';
  }
  return 'Something went wrong while the console checked your passkey. Nothing is wrong with your account. Try again; if it keeps happening, copy the technical detail below.';
}

function renderAccess(block, state, opts, handlers) {
  const o = opts || {};
  const family = state.startsWith('admin') ? 'admin' : 'access';
  const sameFamily = block.dataset.family === family;
  const sameState = sameFamily && block.dataset.state === state;
  const prevSteps = sameFamily ? String(block.dataset.steps || '').split(',') : [];
  block.dataset.family = family;
  block.dataset.state = state;
  // Within one flow the steps stay put and only what changed moves (the video:
  // the heading swaps, a tick lands, the next ring starts).
  const rise = (delay) => (sameFamily && delay > 0 ? '' : ` si2-rise" style="animation-delay:${delay}ms`);
  const still = (delay) => (sameState ? '' : ` si2-rise" style="animation-delay:${delay}ms`);   // unchanged on a redraw of the same state
  let stepStates = [];
  const email = o.email || '';
  let html = '';

  if (state === 'setup' || state === 'joined' || state === 'failed') {
    const opening = o.step === 'opening';
    const findSub = state === 'joined'
      ? [o.addedBy ? `Added by ${o.addedBy}` : '', o.addedOn ? `on ${o.addedOn}` : ''].filter(Boolean).join(' ')
      : state === 'failed' && o.failedStep !== 'opening' ? (o.detail || 'Track didn’t answer')
        : opening ? '' : 'Usually a second or two';
    const steps = [
      { label: 'Signed in', state: 'done' },
      { label: state === 'joined' || opening || (state === 'failed' && o.failedStep === 'opening') ? 'Invitation found' : 'Finding your invitation',
        state: state === 'failed' && o.failedStep !== 'opening' ? 'fail' : (state === 'joined' || opening ? 'done' : 'active'), sub: findSub },
      { label: 'Opening Track',
        state: state === 'failed' ? (o.failedStep === 'opening' ? 'fail' : 'todo') : (state === 'joined' || opening ? 'active' : 'todo'),
        sub: state === 'failed' && o.failedStep === 'opening' ? (o.detail || '') : '' },
    ];
    if (state === 'setup') {
      html = `<div class="si2-head${still(0)}">${head('Setting up your access', 'One moment')}</div>
        <div class="${still(60)}">${googleChip(email, true)}</div>`;
    } else if (state === 'joined') {
      const sub = o.role ? `Joining as ${roleWords(o.role)}` : '';
      html = `<div class="si2-head si2-rise">${head('Welcome', 'You’re in')}</div>
        <div class="si2-rise" style="animation-delay:60ms">${o.company ? companyChip(o.company, sub) : googleChip(email, false)}</div>`;
    } else {
      html = `<div class="si2-head si2-rise">${head(o.label || 'Couldn’t finish', o.heading || 'Track didn’t answer the last step', true)}</div>
        <div class="si2-rise" style="animation-delay:60ms">${googleChip(email, false)}</div>`;
    }
    html += `<div class="si2-stepwrap${rise(120)}">${stepList(steps, prevSteps)}</div>`;
    stepStates = steps.map((st) => st.state);
    if (state === 'failed') {
      html += `<div class="si2-rise" style="animation-delay:160ms">
        <p class="si2-text">${escapeHtml(o.text || 'Nothing is wrong with your account. Try again; if it keeps happening, send the reference below to whoever invited you.')}</p>
        <div class="si2-actions"><button type="button" class="si2-btn state-btn" data-act="retry"><span>Try again</span></button><button type="button" class="si2-link" data-act="signout">Sign out</button></div>
        ${o.ref ? `<div class="si2-ref">Reference <span class="si2-ref-code">${escapeHtml(o.ref)}</span>${o.time ? ` · ${escapeHtml(o.time)}` : ''}</div>` : ''}
      </div>`;
    }
  } else if (state === 'notadded') {
    const who = email ? `<strong>${escapeHtml(email)}</strong>` : 'this account';
    html = `<div class="si2-rise">
        ${head('Not added yet', 'Nobody has added this address', true)}
        <p class="si2-text">${NEUTRAL()
          ? `No company on Track has added ${who}. Ask whoever runs Track at your company to add this exact address, or open the invite they emailed you on this device.`
          : `Nobody has added ${who} to Track yet. Ask a Director to add this exact address, then try again.`}</p>
      </div>
      <div class="si2-rise" style="animation-delay:80ms">
        <div class="si2-actions">
          <button type="button" class="si2-btn state-btn" data-act="retry"><span>Try again</span></button>
          ${email ? '<button type="button" class="si2-btn si2-btn-ghost state-btn" data-act="copy"><span>Copy my address</span></button>' : ''}
        </div>
        <button type="button" class="si2-link si2-link-solo" data-act="switch">Use a different account</button>
      </div>`;
  } else if (state === 'noroles') {
    const chipSub = ['Added you' + (o.addedOn ? ` on ${o.addedOn}` : ''), 'no role set'].join(' · ');
    const owner = o.owner
      ? `<strong>${escapeHtml(o.owner)}</strong>, the Owner,`
      : (NEUTRAL() ? 'your company’s Owner' : 'a Director');
    html = `<div class="si2-rise">${head('No role yet', 'You’re in, but without a role', true)}</div>
      <div class="si2-rise" style="animation-delay:60ms">${o.company ? companyChip(o.company, chipSub) : googleChip(email, false)}
        <p class="si2-text">A role decides what you can see and do. Ask ${owner} to give you one. Track lets you in the moment they do.</p>
      </div>
      <div class="si2-rise" style="animation-delay:120ms"><div class="si2-actions">
        <button type="button" class="si2-btn state-btn" data-act="retry"><span>Try again</span></button>
        <button type="button" class="si2-link" data-act="switch">Use a different account</button>
      </div></div>`;
  } else if (state === 'admin-confirm') {
    html = `<div class="si2-head${still(0)}">${head('Platform admin', 'Confirm it’s you')}</div>
      <div class="si2-rise" style="animation-delay:60ms">${googleChip(email, true)}
        <p class="si2-text">${o.cancelled ? 'The passkey check was cancelled, so the console stayed closed. ' : ''}The admin console runs every company on Track, so it opens only after a passkey check on this device, every time.</p>
      </div>
      <div class="si2-rise" style="animation-delay:120ms">
        <div class="si2-actions"><button type="button" class="si2-btn state-btn" data-act="passkey">${PASSKEY_SVG}<span>Use passkey</span></button><button type="button" class="si2-link" data-act="signout">Sign out</button></div>
        <p class="si2-fine">No passkey on this account yet? Use passkey makes one on this device first, then checks it. Made one on another device? Your browser can use your phone.</p>
      </div>`;
  } else if (state === 'admin-checking') {
    html = `<div class="si2-head${rise(sameFamily ? 1 : 0)}">${head('Platform admin', 'Confirm it’s you')}</div>
      <div class="${rise(sameFamily ? 1 : 60)}">${googleChip(email, true)}</div>
      <div class="si2-wait si2-rise" style="animation-delay:60ms" role="status">
        <span class="si2-wait-ico" aria-hidden="true">${PASSKEY_SVG}</span>
        <span class="si2-chip-txt"><span class="si2-chip-name">Waiting for your passkey</span><span class="si2-chip-sub">Use Touch ID, or your phone, when your browser asks</span></span>
      </div>
      <div class="si2-stepwrap si2-rise" style="animation-delay:120ms">${stepList([
        { label: 'Signed in', state: 'done' },
        { label: 'Checking your passkey', state: 'active' },
        { label: 'Opening the console', state: 'todo' },
      ], prevSteps)}</div>
      <button type="button" class="si2-link si2-link-solo si2-rise" style="animation-delay:160ms" data-act="signout">Cancel and sign out</button>`;
  } else if (state === 'admin-failed') {
    const host = o.page || location.host;
    const detail = `${o.raw || 'Unknown error'}\nPage: ${host} · ${o.time || clockTime()}`;
    html = `<div class="si2-rise">${head('Passkey didn’t work', 'The console couldn’t check your passkey', true)}</div>
      <div class="si2-rise" style="animation-delay:60ms">${googleChip(email, false)}
        <p class="si2-text">${passkeyWords(o.raw, host)}</p>
      </div>
      <div class="si2-rise" style="animation-delay:120ms">
        <div class="si2-actions"><button type="button" class="si2-btn state-btn" data-act="retry"><span>Try again</span></button><button type="button" class="si2-link" data-act="signout">Sign out</button></div>
        <details class="si2-detail" open>
          <summary>Technical detail</summary>
          <pre class="si2-detail-box">${escapeHtml(detail)}</pre>
          <button type="button" class="si2-link si2-fine-link" data-act="copy-detail"><span>Copy this if you’re reporting it</span></button>
        </details>
      </div>`;
    block.dataset.detail = detail;
  }

  block.dataset.steps = stepStates.join(',');
  block.innerHTML = html;
  const on = (act, fn) => block.querySelectorAll(`[data-act="${act}"]`).forEach((b) => b.addEventListener('click', fn));
  on('retry', () => handlers.onRetry && handlers.onRetry());
  on('switch', () => handlers.onSwitch && handlers.onSwitch());
  on('signout', () => handlers.onSignOut && handlers.onSignOut());
  on('passkey', () => handlers.onPasskey && handlers.onPasskey());
  on('copy', (e) => copyText(email, e.currentTarget));
  on('copy-detail', (e) => copyText(block.dataset.detail || '', e.currentTarget));
}
